import React, { SyntheticEvent, useEffect } from 'react'
import { Link as GatsbyLink } from 'gatsby'
import {
  Box, Button, SxProps, Tab, Typography, useTheme
} from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { createGlobalState } from 'react-hooks-global-state'

import { useHeaderHeight } from '@components/Header'
import { Icon } from '@components/Icon'
import { rudderslabTrackOnClick } from '~/utils/common'

export type PricingCardType = {
  title: string
  smalltitle: string
  button: {
    btnexternallink: boolean
    btnlink: string
    btntext: string
  }
  eventCount: string
  pricePerMonth: string
  showPerMonth: boolean
  pricingTopLabel: string
  pricing_features: string[]
  sx?: SxProps
}

export const PricingCard = ({
  title,
  smalltitle,
  pricingTopLabel,
  pricePerMonth,
  showPerMonth,
  eventCount,
  pricing_features: pricingFeatures,
  button,
  sx
}: PricingCardType) => {
  const { palette, spacing } = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: 1,
        mx: {
          xs: 1.5,
          tablet: 1,
          desktop: 1.5
        },
        borderWidth: 1,
        borderRadius: 2,
        borderColor: 'border.main',
        p: 3,
        bgcolor: 'background.default',
        ...sx
      }}>
      <Typography variant="h6" sx={{
        fontSize: {
          xs: '1.5rem',
          tablet: '1.25rem'
        }
      }} gutterBottom>{title}</Typography>
      <Typography
        color='text.secondary'
        sx={{
          mb: 2,
          minHeight: {
            xs: spacing(6),
            tablet: spacing(10)
          }
        }}>
        {smalltitle}
      </Typography>
      <Typography variant='subtitle2' color='text.disabled' gutterBottom>{pricingTopLabel}</Typography>
      <Typography variant="h4" color='primary.main' fontWeight={500} mb={0.75}>
        <span>{pricePerMonth}</span>
        {showPerMonth && (
          <Typography
            component="span"
            variant="subtitle1"
            ml={1}
            color='text.primary'
            fontWeight={400}>
            /month
          </Typography>
        )}
      </Typography>
      <Typography variant='subtitle2' color='text.disabled' mb={3}>{eventCount}</Typography>
      {pricingFeatures?.map((feature, i) => (
        <Box key={feature} display="flex" alignItems="center" mb={i === pricingFeatures.length - 1 ? 3 : 1.5}>
          {i !== 0 && <Icon icon="check" color={palette.success.light} sx={{ mr: 2 }} />}
          <Typography fontWeight={i === 0 ? 500 : 400}>{feature}</Typography>
        </Box>
      ))}
      {button && (
        <Button
          variant="contained"
          component={button.btnexternallink ? 'a' : GatsbyLink}
          {...{
            [button.btnexternallink ? 'href' : 'to']: button.btnlink
          }}
          sx={{
            mt: 'auto',
            width: {
              xs: '100%',
              tablet: 'auto'
            }
          }}
          onClick={e => rudderslabTrackOnClick('button', title, e)}
        >
          {button.btntext}
        </Button>
      )}
    </Box>
  )
}

export const { useGlobalState: useMobilePricingTabs } = createGlobalState({ tabs: '' });
export const MobilePricingTabs = ({ pricingCards }: { pricingCards: PricingCardType[] }) => {
  const tabs = pricingCards.map(({ title }) => title)
  const [activeTab, setActiveTab] = useMobilePricingTabs('tabs')
  const [height] = useHeaderHeight('height')

  useEffect(() => {
    setActiveTab(tabs[0])
    // on mount ONLY
    // eslint-disable-next-line
  }, [])

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        display: {
          xs: 'block',
          tablet: 'none'
        }
      }}>
      <TabContext value={activeTab}>
        <TabList
          onChange={(e: SyntheticEvent, newTab: typeof tabs[number]) => setActiveTab(newTab)}
          aria-label='Integrations Tabs'
          sx={{
            position: 'sticky',
            top: height,
            zIndex: 2,
            bgcolor: 'background.default', 
            mx: -2,
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'border.main',
            button: {
              flex: 1,
              borderRight: 1,
              borderColor: 'border.main',
              '&:last-child': {
                borderRight: 0
              }
            },
          }}>
          {tabs.map(title => (<Tab key={title} label={title} value={title} />))}
        </TabList>
        {pricingCards.map(pricingCard => (
          <TabPanel
            key={pricingCard.title}
            value={pricingCard.title}
            sx={{
              py: 0,
              px: 2,
              mx: -2,
              bgcolor: 'background.default' 
            }}
          >
            <PricingCard
              {...pricingCard}
              sx={{
                px: {
                  xs: 1,
                  mobile: 3,
                },
                py: {
                  xs: 3,
                  mobile: 5,
                },
                pb: {
                  xs: 0,
                  mobile: 5
                },
                borderWidth: 0,
                mx: 0
              }} />
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  )
}